<template>
  <div
    class="mc-button-wrapper"
    :class="{ 'w-100': block }"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
  >
    <b-button
      v-bind="$attrs"
      :variant="computedVariant"
      :class="`mc-button mc-btn__${variant} ${computedClass} ${block ? 'block-btn' : ''} ${
        borderNone ? 'mc-btn__border-none' : ''
      }`"
      :disabled="disabled"
      v-on="$listeners"
      @click="createLog"
    >
      <!-- Todo: add slot for upcoming feature -->
      <slot name="before-slot" />
      <i v-if="icon && iconPosition === 'left'" :class="`mc-icon ${icon}`" />
      {{ text }}
      <i v-if="icon && iconPosition === 'right'" :class="`mc-icon ${icon}`" />
      <slot name="after-slot" />
    </b-button>
    <div v-if="tooltipVisible && displayTooltip" class="tooltip" :data-position="tooltipPosition">
      {{ tooltipText }}
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
export default {
  mixins: [Helper],
  props: {
    text: {
      type: String,
      default: 'Button',
    },
    variant: {
      type: String,
      default: 'primary',
    },
    size: {
      type: String,
    },
    icon: {
      type: String,
    },
    iconPosition: {
      type: String,
      default: 'left',
    },
    disabled: {
      type: Boolean,
    },
    displayTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
    },
    block: {
      type: Boolean,
      default: false,
    },
    borderNone: {
      type: Boolean,
      default: false,
    },
    tooltipPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['top', 'right', 'bottom', 'left'].includes(value),
    },
  },

  data() {
    return {
      tooltipVisible: false,
    }
  },
  computed: {
    computedVariant() {
      const defaultVariant = `${this.variant ? this.variant : 'primary'}`
      // const defaultVariant = 'primary'
      const variantMapping = {
        secondary: 'outline-primary',
        minimal: 'outline-primary',
        'secondary-red': 'outline-danger',
        success: 'success',
        warning: 'warning',
        info: 'info',
        'secondary-warning': 'outline-warning',
        'secondary-success': 'outline-success',
      }

      return variantMapping[this.variant] || defaultVariant
    },
    computedClass() {
      const buttonClasses = []
      if (this.icon && !this.text) buttonClasses.push('mc-btn__icon')
      if (this.size) buttonClasses.push(`mc-btn__${this.size}`)

      return buttonClasses.join(' ')
    },
  },
  methods: {
    showTooltip() {
      if (this.disabled) {
        this.tooltipVisible = true
      }
    },
    hideTooltip() {
      this.tooltipVisible = false
    },
    createLog() {
      this.userLogging('ButtonClick', this.$route, this.text)
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-button-wrapper {
  position: relative;
  display: inline-block;
  vertical-align: top;
}

.tooltip {
  position: absolute;
  background-color: $mep-color-text-400;
  color: #fff;
  padding: 5px 10px;
  border-radius: 4px;
  white-space: nowrap;
  font-size: 12px;
  visibility: visible;
  opacity: 1;
  transition: 0.3s;

  &[data-position='left'] {
    right: calc(100% + 5px);
    top: 50%;
    transform: translateY(-50%);
  }

  &[data-position='right'] {
    left: calc(100% + 5px);
    top: 50%;
    transform: translateY(-50%);
  }

  &[data-position='top'] {
    bottom: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%);
  }

  &[data-position='bottom'] {
    top: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%);
  }
}

.mc-button {
  display: flex;
  gap: 8px;
  padding: 8px 16px 8px 16px;

  &.block-btn {
    width: 100%;
    justify-content: center;
  }

  .mc-icon {
    vertical-align: bottom;
  }

  .mc-icon:before {
    font-size: 20px;
  }

  &.mc-btn__icon {
    padding: 8px;
  }
  &.mc-btn__small,
  &.mc-btn__sm {
    gap: 4px;
    padding: 4px 8px 4px 8px;
    border-radius: 3px;
    font-size: 11px;

    &.mc-btn__icon {
      padding: 4px;
    }

    .mc-icon:before {
      font-size: 16px;
    }

    .mc-icon {
      font-size: 16px;
    }
  }
  &.mc-btn__full {
    width: 100%;
    text-align: center; // Align text to the center
    display: flex; // Use flexbox for centering
    justify-content: center; // Center content horizontally
    align-items: center; // Center content vertically
  }

  &.mc-btn__primary {
    .mc-icon:before {
      color: $white;
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: $gray;
      border-color: transparent;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }

    &:focus {
      outline: 2px solid $mep-primary-200 !important;
    }
  }

  &.mc-btn__secondary {
    .mc-icon:before {
      color: $mep-primary;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white;
      background-color: $mep-primary;

      .mc-icon:before {
        color: $white;
      }
    }

    &:focus {
      outline: 2px solid $mep-primary-200 !important;
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: $white;
      border-color: $mep-color-text-200;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }

    &.mc-btn__table {
      display: block;
      margin: 7px auto;
      text-align: center;
    }
  }

  &.mc-btn__outline-primary {
    &:disabled {
      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }
  }

  &.mc-btn__secondary-red {
    color: $red;
    background-color: $white;
    border-color: $red;
    .mc-icon:before {
      color: $red;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white;
      background-color: $red;
      border-color: $red;
      .mc-icon:before {
        color: $white;
      }
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: $white;
      border-color: $mep-color-text-200;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }
  }

  &.mc-btn__minimal {
    border-color: transparent;

    .mc-icon:before {
      color: $mep-primary;
    }

    &:hover,
    &:active,
    &:focus {
      color: $white;
      background-color: $mep-primary;

      .mc-icon:before {
        color: $white;
      }
    }

    &:disabled {
      color: $mep-color-text-200;
      background-color: transparent;

      .mc-icon:before {
        color: $mep-color-text-200;
      }
    }
  }

  &.mc-btn__border-none {
    border: none !important;
  }

  &.mc-btn__success {
    color: $white;
    background-color: $green;
    border-color: $green;
    .mc-icon:before {
      color: $white;
    }
  }

  &.mc-btn__secondary-success {
    .mc-icon:before {
      color: $green;
    }

    &:hover,
    &:active,
    &:focus {
      .mc-icon:before {
        color: $white;
      }
    }

    &.disabled {
      color: $teal-300;
      border-color: $teal-300;

      .mc-icon:before {
        color: $teal-300;
      }
    }
  }

  &.mc-btn__info {
    background-color: $blue-100;
    color: rgba(28, 116, 218, 1);
    border-color: $blue-100;
    .mc-icon:before {
      color: rgba(28, 116, 218, 1);
    }

    &:hover,
    &:active,
    &:focus {
      opacity: 0.8;
    }
  }
}

.btn-download {
  .mc-button {
    width: 100% !important;
    margin-top: 10px !important;
    justify-content: center !important;
  }
}
</style>
